import { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
  Button,
  Input,
  Textarea,
  MultiSelect,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { INDUSTRY_OPTIONS, CUSTOMER_SOURCES } from '../new/constants';
import { useUpdateContact, type CustomerContact } from '@/api/customer-basic';

/** Contact roles — legacy `CONTACT_ROLE_OPTIONS` values (EN-first). */
const ROLE_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'collaborator', label: 'Collaborator' },
  { value: 'affiliate', label: 'Affiliate' },
];

/** Contact language — mirrors the legacy edit form's fixed set. */
const LANGUAGE_OPTIONS = [
  { value: 'vi', label: 'Tiếng Việt (vi)' },
  { value: 'en', label: 'English (en)' },
  { value: 'ja', label: '日本語 (ja)' },
  { value: 'ko', label: '한국어 (ko)' },
  { value: 'zh', label: '中文 (zh)' },
];

/** Free-form contact labels stored on `contact_label` (creatable). */
type FormState = {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_position: string;
  contact_industry: string;
  contact_source: string;
  contact_roles: string[];
  contact_label: string[];
  contact_language: string;
  contact_note: string;
};

/** Build the editable form state from a contact record. */
function toForm(c: CustomerContact): FormState {
  return {
    contact_name: c.contact_name ?? '',
    contact_email: c.contact_email ?? '',
    contact_phone: c.contact_phone ?? '',
    contact_position: c.contact_position ?? '',
    contact_industry: c.contact_industry ?? '',
    contact_source: c.contact_source ?? '',
    contact_roles: c.contact_roles?.length ? c.contact_roles : ['customer'],
    contact_label: c.contact_label ?? [],
    contact_language: c.contact_language ?? 'vi',
    contact_note: c.contact_note ?? '',
  };
}

export interface ContactEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: number;
  contact: CustomerContact;
}

/**
 * Slide-over edit form for a single contact. Mirrors the legacy
 * `contact-edit-form` fields (name/email/phone/position/industry/source/roles/
 * labels/language/note) and PUTs via `useUpdateContact`. Followers/affiliate
 * pickers (async employee/affiliate selects) are intentionally out of scope.
 */
export function ContactEditDialog({ open, onOpenChange, customerId, contact }: ContactEditDialogProps) {
  const [form, setForm] = useState<FormState>(() => toForm(contact));
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateContact(customerId);

  // Reset the form whenever a different contact is opened or the panel re-opens.
  useEffect(() => {
    if (open) {
      setForm(toForm(contact));
      setError(null);
    }
  }, [open, contact]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const nameInvalid = form.contact_name.trim() === '';
  const emailInvalid = form.contact_email.trim() === '';

  const handleSave = () => {
    if (nameInvalid || emailInvalid) {
      setError('Name and email are required.');
      return;
    }
    setError(null);
    update.mutate(
      {
        contactId: contact.customer_contact_id,
        payload: {
          contact_name: form.contact_name,
          contact_email: form.contact_email,
          contact_phone: form.contact_phone,
          contact_position: form.contact_position || undefined,
          contact_industry: form.contact_industry || undefined,
          contact_source: form.contact_source || undefined,
          contact_roles: form.contact_roles.length ? form.contact_roles : ['customer'],
          contact_label: form.contact_label,
          contact_language: form.contact_language || 'vi',
          contact_note: form.contact_note || undefined,
        },
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: () => setError('Failed to save the contact. Please try again.'),
      },
    );
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" size="md">
        <DrawerHeader>
          <DrawerTitle>Edit contact</DrawerTitle>
          <DrawerDescription>{contact.contact_email}</DrawerDescription>
        </DrawerHeader>
        <DrawerBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {error ? <Alert tone="danger" title={error} /> : null}
            <Field label="Contact name" required error={nameInvalid ? 'Required' : undefined}>
              {(p) => (
                <Input
                  {...p}
                  value={form.contact_name}
                  invalid={nameInvalid}
                  onChange={(e) => set('contact_name', e.target.value)}
                />
              )}
            </Field>
            <Field label="Email" required error={emailInvalid ? 'Required' : undefined}>
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  value={form.contact_email}
                  invalid={emailInvalid}
                  onChange={(e) => set('contact_email', e.target.value)}
                />
              )}
            </Field>
            <Field label="Phone">
              {(p) => (
                <Input {...p} value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} />
              )}
            </Field>
            <Field label="Position">
              {(p) => (
                <Input
                  {...p}
                  value={form.contact_position}
                  onChange={(e) => set('contact_position', e.target.value)}
                />
              )}
            </Field>
            <Field label="Industry">
              {(p) => (
                <Select value={form.contact_industry} onValueChange={(v) => set('contact_industry', v)}>
                  <SelectTrigger id={p.id}>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Source">
              {(p) => (
                <Select value={form.contact_source} onValueChange={(v) => set('contact_source', v)}>
                  <SelectTrigger id={p.id}>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_SOURCES.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Roles">
              {() => (
                <MultiSelect
                  placeholder="Select roles"
                  options={ROLE_OPTIONS}
                  value={form.contact_roles}
                  onChange={(v) => set('contact_roles', v)}
                />
              )}
            </Field>
            <Field label="Labels" description="Free-form tags for this contact.">
              {() => (
                <MultiSelect
                  placeholder="Add labels"
                  options={form.contact_label.map((l) => ({ value: l, label: l }))}
                  value={form.contact_label}
                  onChange={(v) => set('contact_label', v)}
                  creatable
                />
              )}
            </Field>
            <Field label="Language">
              {(p) => (
                <Select value={form.contact_language} onValueChange={(v) => set('contact_language', v)}>
                  <SelectTrigger id={p.id}>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Note">
              {(p) => (
                <Textarea
                  {...p}
                  rows={3}
                  value={form.contact_note}
                  onChange={(e) => set('contact_note', e.target.value)}
                />
              )}
            </Field>
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={update.isPending}>
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default ContactEditDialog;
